// Library Methods
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import { useTheme } from "@mui/material/styles";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Types
import {
  ProgressionExamSummaryType,
  SelectedProgressionExamType,
} from "src/services/exams/types";

// Utils
import { getVisualField } from "src/utils/exams/examReportsHelper";
import { getLocalDate } from "src/utils/date/dateHelper";
import { getExamStats } from "src/utils/exams/progressionAnalysisHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

type ExamAnalysisCardType = {
  examSection: ProgressionExamSummaryType;
  selectedExamSection: SelectedProgressionExamType | undefined;
  isSelected: boolean;
  isDisabled: boolean;
  handleExamSelect: (examSection: ProgressionExamSummaryType) => Promise<void>;
};

const ExamAnalysisCard = ({
  examSection,
  selectedExamSection,
  isSelected,
  isDisabled,
  handleExamSelect,
}: ExamAnalysisCardType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();
  const theme = useTheme();
  const { elevatedSurface: unselectedCardBackground } = getSurfaceTokens(theme);

  const [showLoadingText, setShowLoadingText] = useState(false);

  const examTitle = useMemo(
    () => examSection?.algorithm ?? "",
    [examSection?.algorithm]
  );

  const gridTypeLabel = useMemo(
    () => getVisualField(examSection?.gridType),
    [examSection?.gridType]
  );

  const completionDate = useMemo(() => {
    if (examSection?.completionDate) {
      return getLocalDate(examSection?.completionDate, clinicInfo?.timezone);
    }
    return "N/A";
  }, [examSection?.completionDate, clinicInfo?.timezone]);

  const stats = useMemo(
    () => getExamStats(selectedExamSection?.reportData),
    [selectedExamSection?.reportData]
  );

  return (
    <Box
      data-testid={`select-analysis-card-${examSection.id}`}
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: "420px",
        borderRadius: "8px",
        backgroundColor: isSelected ? "#E2772E" : unselectedCardBackground,
        padding: "16px",
        gap: "16px",
        cursor: isDisabled ? "not-allowed" : "pointer",
      }}
      onClick={async () => {
        if (!isDisabled) {
          // only show loading text when first selecting the card
          if (!isSelected) {
            setShowLoadingText(true);
          }
          await handleExamSelect(examSection);
          setShowLoadingText(false);
        }
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", gap: "16px", alignItems: "center" }}>
          <Checkbox
            disabled={isDisabled}
            checked={isSelected}
            sx={{
              padding: 0,
              color: "#FFF",
              "&.Mui-checked": { color: "#FFF" },
              "&.Mui-disabled": { color: "#FFF" },
            }}
          />
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 600, color: "#FFF" }}
            >
              {t(examTitle)} | {gridTypeLabel}
            </Typography>
            <Box sx={{ display: "flex", gap: "16px" }}>
              <Typography sx={{ fontSize: "14px", color: "#FFF" }}>
                {completionDate}
              </Typography>{" "}
              <Typography sx={{ fontSize: "14px", color: "#FFF" }}>
                <span style={{ fontWeight: 600 }}>{t("Age")}:</span>{" "}
                {examSection.patientAge}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: "8px",
            backgroundColor: "#FFF",
            borderRadius: "2px",
          }}
        >
          <Typography
            sx={{
              color: "#E2772E",
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            {t("RetinaLogik")}
          </Typography>
        </Box>
      </Box>

      {Boolean(selectedExamSection) ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <Grid container>
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "14px", color: "#FFF" }}>
                <span style={{ fontWeight: 600 }}>{t("FL")}:</span> {stats.FL}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "14px", color: "#FFF" }}>
                <span style={{ fontWeight: 600 }}>{t("FN")}:</span> {stats.FN}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "14px", color: "#FFF" }}>
                <span style={{ fontWeight: 600 }}>{t("FP")}:</span> {stats.FP}
              </Typography>
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "14px", color: "#FFF" }}>
                <span style={{ fontWeight: 600 }}>{t("PSD")}:</span> {stats.PSD}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "14px", color: "#FFF" }}>
                <span style={{ fontWeight: 600 }}>{t("MD")}:</span> {stats.MD}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "14px", color: "#FFF" }}>
                <span style={{ fontWeight: 600 }}>{t("VRFI")}:</span>{" "}
                {stats.VRFI}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box sx={{ height: "50px" }}>
          {showLoadingText && (
            <Typography
              sx={{ fontSize: "14px", fontWeight: 600, color: "#FFF" }}
            >
              {t("Loading...")}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ExamAnalysisCard;
